import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Editable fields
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [ifscCode, setIfscCode] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('employeeToken');
      const res = await fetch('/api/employee/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.success && result.data) {
        const emp = result.data;
        setProfile(emp);
        setPhone(emp.phone || '');
        setAddress(emp.address || '');
        setEmergencyContact(emp.emergency_contact || '');
        setBankName(emp.bank_name || '');
        setBankAccount(emp.bank_account || '');
        setIfscCode(emp.ifsc_code || '');
      } else {
        setError(result.error || 'Failed to fetch profile data');
      }
    } catch (err) {
      setError(err.message || 'Error fetching profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('employeeToken');
      const res = await fetch('/api/employee/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone,
          address,
          emergency_contact: emergencyContact,
          bank_name: bankName,
          bank_account: bankAccount,
          ifsc_code: ifscCode
        })
      });

      const result = await res.json();
      if (result.success) {
        setSuccess('Profile details updated successfully.');
        fetchProfile();
      } else {
        setError(result.error || 'Failed to update profile.');
      }
    } catch (err) {
      setError(err.message || 'Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const initials = (profile?.full_name || 'EMP')
    .split(' ')
    .map(p => p[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Profile Header */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm flex flex-col sm:flex-row items-center gap-5">
        <div className="w-16 h-16 rounded-full bg-primary/10 text-primary font-bold text-xl flex items-center justify-center border border-primary/20 shrink-0 overflow-hidden">
          {profile?.face_photo_url ? (
            <img src={profile.face_photo_url} alt={profile.full_name} className="w-full h-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <div className="text-center sm:text-left">
          <h2 className="text-xl font-bold text-foreground">{profile?.full_name || 'Employee Profile'}</h2>
          <p className="text-xs text-muted-foreground">
            {profile?.position || 'Staff'} • <span className="font-semibold text-primary">{profile?.department || 'General'}</span> • Code: <span className="font-mono text-foreground font-bold">{profile?.employee_code || 'N/A'}</span>
          </p>
        </div>
      </div>

      {loading ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
          <Icon name="Loader2" size={28} className="animate-spin text-teal-600 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading profile information...</p>
        </div>
      ) : (
        <form onSubmit={handleSaveProfile} className="space-y-6">
          {error && (
            <div className="p-4 bg-rose-500/10 border border-rose-500/20 text-rose-600 rounded-xl text-xs font-semibold">
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 rounded-xl text-xs font-semibold">
              {success}
            </div>
          )}

          {/* Admin Managed Read-Only Fields */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-border pb-3">
              <Icon name="Shield" size={18} className="text-primary" />
              <h3 className="text-sm font-bold text-foreground">Organization Managed Information (Read-Only)</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground block mb-1">Full Name</span>
                <span className="font-semibold text-foreground">{profile?.full_name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-1">Employee Code</span>
                <span className="font-mono font-semibold text-foreground">{profile?.employee_code || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-1">Official Email</span>
                <span className="font-semibold text-foreground">{profile?.email || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-1">Department</span>
                <span className="font-semibold text-foreground">{profile?.department || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-1">Designation</span>
                <span className="font-semibold text-foreground">{profile?.position || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground block mb-1">Date of Joining</span>
                <span className="font-semibold text-foreground">{profile?.hire_date || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Self-Editable Fields */}
          <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-border pb-3">
              <Icon name="UserCheck" size={18} className="text-teal-600" />
              <h3 className="text-sm font-bold text-foreground">Self-Editable Personal & Banking Contact Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Phone Number</label>
                <Input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 9876543210"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Emergency Contact Number</label>
                <Input
                  type="text"
                  value={emergencyContact}
                  onChange={(e) => setEmergencyContact(e.target.value)}
                  placeholder="+91 9876543210"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-foreground mb-1.5">Residential Address</label>
                <textarea
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street, City, State, Pincode"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs focus:ring-1 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Bank Name</label>
                <Input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="e.g. HDFC Bank"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Bank Account Number</label>
                <Input
                  type="text"
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                  placeholder="e.g. 5010023456789"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">IFSC Code</label>
                <Input
                  type="text"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  placeholder="e.g. HDFC0001234"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-border flex justify-end">
              <Button
                type="submit"
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6"
                iconName={saving ? "Loader2" : "Save"}
                iconPosition="left"
              >
                {saving ? "Saving Changes..." : "Save Profile Changes"}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default MyProfile;
